import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";

const pad = (n) => String(n).padStart(2, "0");
// ISO (UTC) -> value for <input type="datetime-local"> in the browser's local zone
export const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (v) => (v ? new Date(v).toISOString() : null);
const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

export const entryHours = (e) => {
  const now = Date.now();
  const ci = new Date(e.clock_in).getTime();
  const co = e.clock_out ? new Date(e.clock_out).getTime() : now;
  let ms = co - ci;
  for (const b of e.breaks || []) ms -= (b.end ? new Date(b.end).getTime() : Math.min(now, co)) - new Date(b.start).getTime();
  return Math.max(ms, 0) / 3600000;
};

const inputCls = "w-full px-3 py-2 rounded-lg border border-stone-300 bg-white outline-none focus:border-brand-red text-sm font-mono";

export function TimeEntryEditor({ token, entry, onClose, onSaved }) {
  const [clockIn, setClockIn] = useState(toLocalInput(entry.clock_in));
  const [clockOut, setClockOut] = useState(toLocalInput(entry.clock_out) || (entry.clock_out ? "" : toLocalInput(new Date().toISOString())));
  const [breaks, setBreaks] = useState((entry.breaks || []).map((b) => ({ start: toLocalInput(b.start), end: toLocalInput(b.end) })));
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const isOpen = !entry.clock_out;

  const save = async (e) => {
    e.preventDefault();
    if (!clockIn) return toast.error("Clock-in time is required");
    setSaving(true);
    try {
      const body = {
        clock_in: fromLocalInput(clockIn),
        clock_out: fromLocalInput(clockOut),
        breaks: breaks.filter((b) => b.start).map((b) => ({ start: fromLocalInput(b.start), end: fromLocalInput(b.end) })),
        note,
      };
      const { data } = await adminApi(token).put(`/admin/time-entries/${entry.id}`, body);
      toast.success(isOpen && data.clock_out ? `Shift closed — ${data.hours.toFixed(2)}h` : "Punch updated");
      onSaved(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete this punch for ${entry.employee_name}? This can't be undone.`)) return;
    try {
      await adminApi(token).delete(`/admin/time-entries/${entry.id}`);
      toast.success("Punch deleted");
      onSaved(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="time-entry-editor">
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-md p-5 space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{isOpen ? "Close / fix open shift" : "Edit punch"}</div>
            <div className="font-black text-stone-800 text-lg" data-testid="time-entry-editor-name">{entry.employee_name}</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-stone-400 hover:text-stone-700"><X className="w-5 h-5" /></button>
        </div>
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Clock in</span>
          <input type="datetime-local" value={clockIn} onChange={(e) => setClockIn(e.target.value)} data-testid="time-entry-clock-in" className={inputCls} required />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Clock out {isOpen && <span className="text-amber-600">(currently open — set to close)</span>}</span>
          <div className="flex gap-2">
            <input type="datetime-local" value={clockOut} onChange={(e) => setClockOut(e.target.value)} data-testid="time-entry-clock-out" className={inputCls} />
            <button type="button" onClick={() => setClockOut(toLocalInput(new Date().toISOString()))} data-testid="time-entry-clock-out-now"
              className="shrink-0 px-3 rounded-lg border border-stone-300 text-[10px] uppercase tracking-widest font-bold text-stone-600 hover:bg-stone-100">Now</button>
          </div>
        </label>
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Unpaid breaks ({breaks.length})</span>
            <button type="button" onClick={() => setBreaks([...breaks, { start: "", end: "" }])} data-testid="time-entry-add-break"
              className="text-[10px] uppercase tracking-widest font-bold text-brand-red flex items-center gap-1"><Plus className="w-3 h-3" /> Add break</button>
          </div>
          {breaks.length === 0 && <div className="text-xs text-stone-400 italic">No breaks.</div>}
          <div className="space-y-2">
            {breaks.map((b, i) => (
              <div key={i} className="flex items-start gap-2 bg-stone-50 border border-stone-200 rounded-xl p-2" data-testid={`time-entry-break-${i}`}>
                <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <label className="block min-w-0">
                    <span className="text-[9px] uppercase tracking-widest text-stone-400 font-bold">Break start</span>
                    <input type="datetime-local" value={b.start} onChange={(e) => setBreaks(breaks.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))} className={`${inputCls} min-w-0`} data-testid={`time-entry-break-${i}-start`} />
                  </label>
                  <label className="block min-w-0">
                    <span className="text-[9px] uppercase tracking-widest text-stone-400 font-bold">Break end</span>
                    <input type="datetime-local" value={b.end} onChange={(e) => setBreaks(breaks.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))} className={`${inputCls} min-w-0`} data-testid={`time-entry-break-${i}-end`} />
                  </label>
                </div>
                <button type="button" onClick={() => setBreaks(breaks.filter((_, j) => j !== i))} aria-label="Remove break" className="p-2 mt-4 text-stone-400 hover:text-red-600 shrink-0"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Note (optional)</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. forgot to clock out" data-testid="time-entry-note" className={inputCls.replace("font-mono", "")} />
        </label>
        <div className="flex items-center gap-2 pt-1">
          <button type="submit" disabled={saving} data-testid="time-entry-save"
            className="bg-brand-red text-white px-5 py-2 rounded-full text-xs uppercase tracking-widest font-bold hover:bg-red-800 disabled:opacity-50">
            {saving ? "Saving…" : isOpen && clockOut ? "Close shift" : "Save"}
          </button>
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-stone-300 text-xs uppercase tracking-widest font-bold text-stone-600">Cancel</button>
          <button type="button" onClick={remove} data-testid="time-entry-delete" className="ml-auto text-xs uppercase tracking-widest font-bold text-stone-400 hover:text-red-600 flex items-center gap-1">
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      </form>
    </div>
  );
}

export function TimeEntriesCard({ token, refreshKey, onChanged }) {
  const today = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Chicago" }).format(new Date());
  const [date, setDate] = useState(today());
  const [entries, setEntries] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi(token).get(`/admin/time-entries?date=${date}`);
      setEntries(data);
    } catch {
      toast.error("Couldn't load punches");
    }
  }, [token, date]);
  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="time-entries-card">
      <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="font-black text-stone-800 text-sm uppercase tracking-widest">Punches</div>
          <div className="text-[11px] text-stone-500">Tap a row to fix times, add a missed break, or close a shift. Days run midnight-to-midnight Central.</div>
        </div>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="time-entries-date"
          className="px-3 py-1.5 rounded-lg border border-stone-300 text-sm font-mono" />
      </div>
      {entries.length === 0 ? (
        <div className="p-6 text-center text-stone-400 text-sm" data-testid="time-entries-empty">No punches on this day.</div>
      ) : (
        <ul className="divide-y divide-stone-100">
          {entries.map((e) => (
            <li key={e.id}>
              <button type="button" onClick={() => setEditing(e)} data-testid={`time-entry-row-${e.id}`}
                className="w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-stone-50">
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-stone-800 text-sm flex items-center gap-2 flex-wrap">
                    {e.employee_name}
                    {!e.clock_out && <span className="text-[9px] uppercase tracking-widest bg-emerald-100 text-emerald-800 rounded-full px-1.5 py-0.5">Open</span>}
                    {e.edited_at && <span className="text-[9px] uppercase tracking-widest bg-stone-200 text-stone-600 rounded-full px-1.5 py-0.5" title={e.edit_note || "Edited by admin"}>Edited</span>}
                  </div>
                  <div className="text-[11px] text-stone-500">
                    {fmt(e.clock_in)} → {e.clock_out ? fmt(e.clock_out) : "still clocked in"}
                    {(e.breaks || []).length > 0 && ` · ${e.breaks.length} break${e.breaks.length === 1 ? "" : "s"}`}
                  </div>
                </div>
                <div className="font-mono font-black text-sm text-stone-700">{entryHours(e).toFixed(2)}h</div>
                <Pencil className="w-4 h-4 text-stone-300" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {editing && (
        <TimeEntryEditor token={token} entry={editing} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); onChanged?.(); }} />
      )}
    </div>
  );
}
